import {HttpErrorResponse}                          from "@angular/common/http";
import {Component, OnInit, OnDestroy, ViewChild}    from '@angular/core';
import {FormBuilder, FormGroup, Validators}         from "@angular/forms";
import {NgbActiveModal}                             from "@ng-bootstrap/ng-bootstrap";

import {AngularQuery}                               from "../../services/query/angularquery";
import {Message}                                    from "../../services/message/message";
import {MessageEntry}                               from "../../services/message/message-entry";
import {Sport}                                      from "../../services/sport/sport";
import {SportService}                               from "../../services/sport/sport.service";
import {Tag}                                        from "../../services/tag/tag";
import {TagService}                                 from "../../services/tag/tag.service";

import {Subscription}                               from "rxjs/Subscription";



@Component({
	selector: 'app-checkbox-admin',
	templateUrl: './checkbox-admin.component.html',
	styleUrls: ['./checkbox-admin.component.css']
})



/**
 * Create, edit and remove the Tag entries from the list of checkboxes
 * that display when a given Sport is selected in the story create/edit screen.
 */
export class CheckboxAdminComponent implements OnInit, OnDestroy {

	checkboxList:Tag[];
	checkboxSubscription:Subscription;
	message:Message;
	sport: Sport[] = [];
	sportFound: Subscription;
	tForm: FormGroup;


	/**
	 * Initialize the component.
	 *
	 * @param {NgbActiveModal} activeModal     Used to close the page.
	 * @param {FormBuilder} fb                 Used to create the page objects.
	 * @param {SportService} sportService      The service that provides Sport information.
	 * @param {TagService} tagService          Used when handling Tag objects.
	 */
	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private sportService: SportService,
		private tagService: TagService,
	) {}


	/**
	 * Unsubscribe from Subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.checkboxSubscription) {

			this.checkboxSubscription.unsubscribe();

		}

		if (this.sportFound) {

			this.sportFound.unsubscribe();

		}

	}


	/**
	 * Initialize the form and load the Sport and Tag information.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			code: ['NFL', [Validators.minLength(3), Validators.maxLength(3)]],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.sportFound = this.sportService.getSports().subscribe(

			(response) => {

				// Set the array of Sport objects.
				this.sport = response;
				let code:string = this.tForm.controls['code'].value;
				let sport_id:number = this.getSportId();

				if (-1 === sport_id) {

					me.message = "Unable to find the sport entry for '" + code + "'";
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				} else {

					this.onSportChange(sport_id);

				}

			},

			() => {

				me.message = 'Sport data did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Lets me talk to the Tag typeahead.
	 */
	@ViewChild('tagTypeahead')
	tagTypeahead: any;


	/**
	 * Don't allow the user to close the window if they have unsaved changes.
	 */
	close():void {

		if (! this.tForm.pristine) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

		}

		this.activeModal.close('Close method');

	}


	/**
	 * Get the ID number of the currently selected sport in the radio buttons.
	 *
	 * @returns {number} -1 if something goes wrong; the ID otherwise.
	 */
	private getSportId():number {

		// Get the ID of the selected entry.
		let sport_id:number = -1;
		let code:string = this.tForm.controls['code'].value;
		for (let entry of this.sport) {

			if (code === entry.db_enum) {

				sport_id = entry.id;
				break;

			}

		}

		return sport_id;

	}


	/**
	 * This is used by the Sport radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The 'code' value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(name: string): any {

		if (name === this.tForm.controls['code'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Manage the settings changes needed when a new
	 * Sport is selected from the radio buttons.
	 *
	 * @param {number} id  The id entry for the Sport ('NFL', 'MLB", etc.) from the ReST API.
	 */
	onSportChange(id:number):void {

		if (! this.tForm.pristine) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

		}

		let me:MessageEntry = new MessageEntry();

		if (this.checkboxSubscription) {

			this.checkboxSubscription.unsubscribe();

		}

		this.checkboxSubscription = this.tagService.getCheckboxes(id).subscribe(

			tags => {

				this.checkboxList = tags;
				this.tForm.markAsPristine();

				// Notify the user.
				me.message = 'Ready';
				me.level = 2;
				this.message.add(me);
				this.message.show();

			},

			error => {

				if (error instanceof HttpErrorResponse && 404 === error.status) {

					// Handle the case where there are no entries for a sport.
					this.checkboxList = [];

					// Notify the user.
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);
					this.message.show();

				} else {

					// Notify the user.
					me.message = 'Unable to load the checkbox information';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			}

		);

	}


	/**
	 * Handle the addition of a new tag to the existing
	 * list of tags.
	 *
	 * @param {Tag} tag  The Tag to be added to the list.
	 */
	onTagChange(tag:Tag):void {

		// Add the tag to the list.
		this.checkboxList.push(tag);

		// Mark the page as dirty.
		this.tForm.markAsDirty();

		// Remove the entry from the typeahead INPUT field.
		this.tagTypeahead.typeaheadTagInput.nativeElement.value = '';

	}


	/**
	 * Uncheck the specified Tag from the tags associated
	 * with this article.
	 *
	 * @param {Tag} tag  The Tag object to uncheck.
	 */
	removeTag(tag: Tag): void {

		let remove_id:number = -1;
		for (let entry in this.checkboxList) {

			if (tag.id === this.checkboxList[entry].id) {

				remove_id = parseInt(entry);
				break;

			}

		}

		if (-1 === remove_id) {

			// Notify the user.
			let me:MessageEntry = new MessageEntry();
			me.message = 'Unable to remove tag from list';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;

		}

		this.checkboxList.splice(remove_id, 1);

		// Mark the page as dirty.
		this.tForm.markAsDirty();

	}


	/**
	 * Save the list of checkbox tags for the current sport.
	 */
	saveTags():void {

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Saving ...';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		// Build the query.
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);
		query.setEndpoint(AngularQuery.ENDPOINT_SPORT_CHECKBOXES, this.getSportId().toString());

		// Build the JSON object.
		let post:Object = {};
		let tag_ids:number[] = [];

		for (let entry of this.checkboxList) {

			tag_ids.push(entry.id);

		}

		post['tags'] = tag_ids;

		// Clear any existing subscriptions and save the changed tag list.
		if (this.checkboxSubscription) {

			this.checkboxSubscription.unsubscribe();

		}

		this.checkboxSubscription = this.tagService.post(query.getUrl(), JSON.stringify(post)).subscribe(

			() => {

				me.message = 'Saved updated tag list';
				me.level = 2;
				this.message.add(me);
				this.message.show();

				this.tForm.markAsPristine();

			},

			() => {

				me.message = 'Failed to save updated tag list';
				me.level = 4;
				this.message.add(me);
				this.message.show();

			}

		);

	}

}



// WEBPACK FOOTER //
// ./src/app/admin/checkbox/checkbox-admin.component.ts